import { useEffect, useRef } from "react";

/**
 * Custom hook to manage focus on main content after page navigation.
 * 
 * This hook improves accessibility by:
 * - Complying with WCAG 2.4.3 (Focus Order) - focus moves to main content after navigation
 * - Setting focus to the first interactive element in the main content area
 * - Skipping non-interactive elements like headers, static text, and column headers
 * - Providing a smooth keyboard navigation experience
 * - Using `focus({ focusVisible: true })` when supported so programmatic focus
 *   shows the same visible indicator as keyboard focus (WCAG 2.4.7 Focus Visible)
 * 
 * The hook automatically detects and focuses the first truly interactive element:
 * 1. Form inputs, buttons, or links that appear before any tables/grids
 * 2. Any focusable element that's not in a table header or column header
 * 3. As a fallback, makes the container focusable for screen readers
 * 
 * @param {Object} options - Configuration options
 * @param {boolean} options.isReady - Indicates when the content is fully loaded (default: true)
 * @param {Array|Object} options.dependencies - Additional dependencies to trigger focus (optional)
 * @param {number} options.delay - Delay in milliseconds before focusing (default: 100)
 * @param {boolean} options.skipHeaders - Whether to skip focusing on header elements (default: true)
 * @param {boolean} options.enabled - Whether the hook is enabled (default: true)
 * 
 * @returns {Object} - Returns { mainContentRef } to be attached to the main content container
 * 
 * @example
 * // Basic usage - focus after data loads
 * const { mainContentRef } = useMainContentFocus({
 *   isReady: !loading && data
 * });
 * 
 * return (
 *   <div ref={mainContentRef}>
 *     <h1>Page Title</h1>
 *     <DataGrid rows={data} />
 *   </div>
 * );
 * 
 * @example
 * // With custom dependencies
 * const { mainContentRef } = useMainContentFocus({
 *   isReady: !loading,
 *   dependencies: [data, selectedTab]
 * });
 */
const useMainContentFocus = ({
  isReady = true,
  dependencies = [],
  delay = 100,
  skipHeaders = true,
  enabled = true
} = {}) => {
  const mainContentRef = useRef(null);

  useEffect(() => {
    if (!enabled || !isReady || !mainContentRef.current) return;

    const focusTimer = setTimeout(() => {
      const mainContent = mainContentRef.current;
      
      // Helper function to focus element and trigger MUI's focus-visible styling
      const focusWithEvent = (element) => {
        // Focus the element first
        // TODO: Add focus-visible styling to the element for better UX. Tried for button no matter we use focus mouseover event,
        // "focus-visible", "mui-focusVisible" class, it doesn't work.
        // "focusVisible works for the links
        if (!element || typeof element.focus !== "function") return;
          element.focus({ focusVisible: true });
      };
      
      // Strategy 1: Find all focusable elements in document order
      const focusableElements = mainContent.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), select:not([disabled]), ' +
        'textarea:not([disabled]), a[href], [role="button"]:not([disabled])'
      );
      
      if (skipHeaders && focusableElements.length > 0) {
        // Filter out elements in headers (table headers, column headers, etc.)
        // This ensures we focus on the first interactive element in natural DOM order
        // that isn't in a header (e.g., buttons before tables, form fields, etc.)
        for (let element of focusableElements) {
          const isInHeader = element.closest(
            '.MuiDataGrid-columnHeader, .MuiDataGrid-columnHeaders, ' +
            'thead, th, [role="columnheader"]'
          );
          
          if (!isInHeader) {
            focusWithEvent(element);
            return;
          }
        }
      } else if (focusableElements.length > 0) {
        // If skipHeaders is false, just focus the first element
        focusWithEvent(focusableElements[0]);
        return;
      }

      // Strategy 2: If no interactive elements found, make container focusable
      // This ensures screen readers can access the content
      if (focusableElements.length === 0) {
        mainContent.setAttribute('tabindex', '-1');
        focusWithEvent(mainContent);
      }
    }, delay);

    return () => clearTimeout(focusTimer);
  }, [isReady, delay, skipHeaders, enabled, dependencies]);

  return { mainContentRef };
};

export default useMainContentFocus;

